/**
 * The services piece's standard look (the prototype's Services menu rows).
 * With the showTimes option, each service is a block with its next open
 * times under it (bookings.html's Book page, web-studio-sites D15).
 * Draws the behavior half's state and calls its actions; holds no rule.
 * Draws nothing until there is at least one service.
 */

import { useId, type ReactNode } from 'react';
import { LOCATION_KIND_LABEL, type SiteServiceSummaryDto } from '@telify/shared';

import { partsOf, pieceRoot, usePlaced } from '../../contract/runtime';
import { onLink } from '../../ui/onLink';
import { formatDuration, priceText } from '../../format';
import shared from '../../styles/shared.module.css';
import { Icon, type IconName } from '../../ui/Icon';
import { Photo } from '../../ui/Photo';
import type { MineMark } from '../events-row/contract';
import type { ServicesRuntime } from './useServices';
import css from './Services.module.css';
import { contract } from './contract';

const part = partsOf(contract);

const FORMAT_ICON: Record<SiteServiceSummaryDto['modality'], IconName> = { IN_PERSON: 'pin', ONLINE: 'video', PHONE: 'phone' };

function Price({ service }: { readonly service: SiteServiceSummaryDto }) {
  return (
    <div className={`${css.price} ${service.price.kind === 'FREE' ? css.free : ''}`} {...part('price')}>
      {service.price.kind === 'FROM' && <small>from</small>}
      {priceText(service.price).replace(/^from /, '')}
    </div>
  );
}

/**
 * With the mine option, the client's next booking of a service (web-studio-sites 6.6): its line and its button, which
 * sits above the item's stretched link.
 */
function Mark({ s, mark, actions }: { readonly s: SiteServiceSummaryDto; readonly mark: MineMark; readonly actions: ServicesRuntime['actions'] }) {
  return (
    <p className={css.mine} {...part('mine', mark.state)}>
      {mark.label && (
        <span className={css.mineLine}>
          {mark.state === 'going' && <Icon name="check" small />}
          <span>
            {mark.label}: <b {...part('mineWhen')}>{mark.when}</b>
          </span>
        </span>
      )}
      <a className={`${shared.go} ${css.mineAction}`} href={mark.action.href} onClick={onLink(() => actions.openBooking(s.id))} {...part('mineAction', mark.state)}>
        <u>{mark.action.label}</u>
      </a>
    </p>
  );
}

/**
 * One row of the list or grid: the whole row opens the service. A row the client has booked holds a second link (its
 * mark's button), so the name's link stretches over the row instead of the row being one link.
 */
function Row({
  s,
  mark,
  showPhoto,
  actions,
  children,
}: {
  readonly s: SiteServiceSummaryDto;
  readonly mark: MineMark | undefined;
  readonly showPhoto: boolean;
  readonly actions: ServicesRuntime['actions'];
  readonly children: ReactNode;
}) {
  // A service with no photo has no photo cell; its text starts at the left edge (Gabriel 2026-10-20).
  const className = `${css.row} ${showPhoto && s.photo ? '' : css.noPhoto} ${mark ? css.rowMarked : ''}`;
  return mark ? (
    <div className={className} {...part('item')}>
      {children}
    </div>
  ) : (
    <a className={className} href={s.path} onClick={onLink(() => actions.open(s.id))} {...part('item')}>
      {children}
    </a>
  );
}

/** One service with its next open times: the name opens its page, and so does the whole block around the times. */
function ServiceWithTimes({ s, state, actions, options }: ServicesRuntime & { readonly s: SiteServiceSummaryDto }) {
  const times = state.times[s.id];
  const mark = state.marks[s.id];
  const photo = options.showPhoto && s.photo;
  return (
    <article className={`${css.svc} ${photo ? '' : css.svcNoPhoto}`} {...part('item')}>
      {photo && (
        <div className={css.svcImg} {...part('photo')}>
          <Photo photo={photo} loading="lazy" />
        </div>
      )}
      <div className={css.svcBody}>
        <div className={css.svcTop}>
          <h3 className={`${shared.h3} ${css.svcName}`} {...part('name')}>
            <a className={css.stretch} href={s.path} onClick={onLink(() => actions.open(s.id))}>
              {s.name}
            </a>
          </h3>
          {options.showPrice && <Price service={s} />}
        </div>
        <div className={css.svcMeta}>
          <span {...part('duration')}>{formatDuration(s.durationMinutes)}</span>
          <span aria-hidden="true"> · </span>
          <span {...part('format')}>{LOCATION_KIND_LABEL[s.modality]}</span>
        </div>
        {mark && <Mark s={s} mark={mark} actions={actions} />}
        {options.showSummary && s.summary && (
          <p className={css.svcSum} {...part('summary')}>
            {s.summary}
          </p>
        )}
        <div className={css.ot} {...part('times')}>
          <div className={css.otHead}>
            <h4 className={css.otTitle} {...part('timesHeading')}>
              Next open times
            </h4>
            <span className={css.otZone} {...part('zone')}>
              {state.zoneName}
            </span>
          </div>
          {times?.status === 'ready' && times.days.length > 0 && (
            <div className={css.otDays} {...part('days')}>
              {times.days.map((d) => (
                <div key={d.date} {...part('day')}>
                  <div className={css.otDayName} {...part('dayName')}>
                    {d.name}
                  </div>
                  <div className={css.otDayDate} {...part('dayDate')}>
                    {d.dateLabel}
                  </div>
                  <div className={css.otTimes}>
                    {d.times.map((t) => (
                      <a
                        key={t.startsAt}
                        className={css.otTime}
                        href={t.href}
                        aria-label={`Book ${s.name}, ${d.longLabel} at ${t.label}`}
                        onClick={onLink(() => actions.openTime(s.id, t.startsAt))}
                        {...part('time')}
                      >
                        {t.label}
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          {times?.status === 'ready' && times.days.length === 0 && (
            <p className={css.otNone} {...part('timesEmpty')}>
              No open times right now.
            </p>
          )}
          {times?.status === 'error' && (
            <p className={css.otNone} {...part('timesEmpty')}>
              Open times could not load. Try again in a moment.
            </p>
          )}
          <div className={css.otFoot}>
            <a className={shared.go} href={s.path} onClick={onLink(() => actions.open(s.id))} {...part('book')}>
              <u>See every open time</u>
              <Icon name="arrow" small />
            </a>
          </div>
        </div>
      </div>
    </article>
  );
}

export function ServicesView({ state, actions, options }: ServicesRuntime) {
  // Placed on a custom page (web-studio-sites D4): no band, no frame, no fixed anchor.
  const placed = usePlaced();
  const placedTitle = useId();
  if (state.services.length === 0) return null;
  const grid = options.layout === 'grid';
  const titleId = placed ? placedTitle : 'servicesTitle';
  return (
    <section
      className={placed ? undefined : shared.sec}
      id={placed ? undefined : 'services'}
      aria-labelledby={titleId}
      {...pieceRoot(contract)}
      {...part('section')}
    >
      <div className={placed ? undefined : shared.wrap}>
        {(options.heading || options.eyebrow || options.intro) && (
          <div className={shared.secHead}>
            <div>
              {options.eyebrow && (
                <div className={shared.eyebrow} {...part('eyebrow')}>
                  {options.eyebrow}
                </div>
              )}
              {options.heading && (
                <h2 className={shared.h2} id={titleId} {...part('heading')}>
                  {options.heading}
                </h2>
              )}
            </div>
            {options.intro && (
              <p className={shared.secIntro} {...part('intro')}>
                {options.intro}
              </p>
            )}
          </div>
        )}
        {options.showTimes ? (
          <div className={css.timesList} {...part('list')}>
            {state.services.map((s) => (
              <ServiceWithTimes key={s.id} s={s} state={state} actions={actions} options={options} />
            ))}
          </div>
        ) : (
          <div className={grid ? css.grid : css.menu} {...part('list')}>
            {state.services.map((s) => (
              <Row key={s.id} s={s} mark={state.marks[s.id]} showPhoto={options.showPhoto} actions={actions}>
                {options.showPhoto && s.photo && (
                  <div className={css.rowImg} {...part('photo')}>
                    <Photo photo={s.photo} loading="lazy" />
                  </div>
                )}
                <div className={css.rowText}>
                  <h3 className={`${shared.h3} ${css.rowName}`} {...part('name')}>
                    {state.marks[s.id] ? (
                      <a className={css.stretch} href={s.path} onClick={onLink(() => actions.open(s.id))}>
                        {s.name}
                      </a>
                    ) : (
                      s.name
                    )}
                  </h3>
                  {options.showSummary && s.summary && (
                    <p className={css.rowSum} {...part('summary')}>
                      {s.summary}
                    </p>
                  )}
                  {state.marks[s.id] && <Mark s={s} mark={state.marks[s.id]!} actions={actions} />}
                </div>
                <div className={css.rowMeta}>
                  <span {...part('duration')}>
                    <Icon name="clock" />
                    {formatDuration(s.durationMinutes)}
                  </span>
                  <span {...part('format')}>
                    <Icon name={FORMAT_ICON[s.modality]} />
                    {LOCATION_KIND_LABEL[s.modality]}
                  </span>
                </div>
                {options.showPrice && <Price service={s} />}
                <span className={`${shared.go} ${css.book}`} {...part('book')}>
                  <u>Book</u>
                  <Icon name="arrow" small />
                </span>
              </Row>
            ))}
          </div>
        )}
        {state.hasMore && (
          <div className={css.more}>
            <a className={shared.go} href={state.allServicesPath} onClick={onLink(actions.seeAll)} {...part('see-all')}>
              <u>See all {state.total} services</u>
              <Icon name="arrow" small />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
